import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Car,
  Building2,
  Calendar as CalendarIcon,
  Clock,
  User,
  FileText,
  CreditCard,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Receipt,
  RefreshCw,
  Search,
  MoreVertical,
  Send,
  Ban,
  Eye,
  Filter,
  Download,
  DollarSign,
  TrendingUp,
  ClipboardList,
  History,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgentBookingDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFacility, setFilterFacility] = useState('');
  
  // Dialog states
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Invoice form
  const [invoiceForm, setInvoiceForm] = useState({
    amount: '',
    description: '',
    due_date: ''
  });

  // Get building ID from user context
  const hoaId = user?.selected_building || user?.hoa_id || 'demo_hoa';

  useEffect(() => {
    fetchBookings();
  }, [hoaId, filterStatus, filterFacility]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/bookings/agent/dashboard/${hoaId}`;
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterFacility) params.append('facility_type', filterFacility);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, { credentials: 'include' });

      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
        setStats(data.stats || {});
      } else {
        // Demo data fallback
        setBookings([
          {
            booking_id: 'bkg_demo_001',
            facility_type: 'visitor_parking',
            parking_bay: 'VP1',
            resident_name: 'John Smith',
            unit_number: '12A',
            start_date: new Date().toISOString(),
            end_date: new Date(Date.now() + 86400000 * 2).toISOString(),
            vehicle_registration: 'CA 123 456',
            visitor_name: 'Jane Doe',
            status: 'pending',
            created_at: new Date().toISOString()
          },
          {
            booking_id: 'bkg_demo_002',
            facility_type: 'venue_hire',
            venue_name: 'Clubhouse',
            resident_name: 'Sarah Johnson',
            unit_number: '5B',
            start_date: new Date(Date.now() + 86400000 * 5).toISOString(),
            end_date: new Date(Date.now() + 86400000 * 5 + 14400000).toISOString(),
            purpose: 'Birthday Party',
            expected_guests: 30,
            status: 'invoice_issued',
            invoice_id: 'inv_demo_001',
            created_at: new Date().toISOString()
          }
        ]);
        setStats({
          total: 10,
          pending: 3,
          invoice_issued: 2,
          paid: 4,
          cancelled: 1,
          visitor_parking: 6,
          venue_hire: 4
        });
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!invoiceForm.amount || !invoiceForm.description) {
      toast.error('Please fill in amount and description');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/agent/invoice/${selectedBooking.booking_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: parseFloat(invoiceForm.amount),
          description: invoiceForm.description,
          due_date: invoiceForm.due_date || null
        })
      });

      if (response.ok) {
        toast.success('Invoice generated and sent to resident');
        setShowInvoiceDialog(false);
        setInvoiceForm({ amount: '', description: '', due_date: '' });
        setSelectedBooking(null);
        fetchBookings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate invoice');
      }
    } catch (error) {
      toast.error('Failed to generate invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvoice = async (invoiceId) => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/agent/resend-invoice/${invoiceId}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Invoice reminder sent');
      } else {
        toast.error('Failed to resend invoice');
      }
    } catch (error) {
      toast.error('Failed to resend invoice');
    }
  };

  const handleUpdateStatus = async (bookingId, status, reason = '') => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/agent/status/${bookingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, reason })
      });

      if (response.ok) {
        toast.success(`Booking ${status}`);
        fetchBookings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleViewAuditLogs = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/audit-logs/${bookingId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        setAuditLogs(await response.json());
      } else {
        setAuditLogs([]);
      }
      setShowAuditDialog(true);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge className="bg-amber-100 text-amber-700">Pending Approval</Badge>,
      invoice_issued: <Badge className="bg-blue-100 text-blue-700">Invoice Issued</Badge>,
      paid: <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>,
      confirmed: <Badge className="bg-emerald-100 text-emerald-700">Confirmed</Badge>,
      cancelled: <Badge className="bg-red-100 text-red-700">Cancelled</Badge>,
    };
    return badges[status] || <Badge variant="secondary">{status}</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', { 
      day: 'numeric', 
      month: 'short'
    });
  };

  const filteredBookings = bookings.filter(b => {
    const matchesSearch = 
      b.resident_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.unit_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.booking_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.vehicle_registration?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });

  const getBookingsByTab = () => {
    switch (activeTab) {
      case 'pending':
        return filteredBookings.filter(b => b.status === 'pending');
      case 'invoiced':
        return filteredBookings.filter(b => b.status === 'invoice_issued');
      case 'confirmed':
        return filteredBookings.filter(b => ['paid', 'confirmed'].includes(b.status));
      case 'parking':
        return filteredBookings.filter(b => b.facility_type === 'visitor_parking');
      case 'venue':
        return filteredBookings.filter(b => b.facility_type === 'venue_hire');
      default:
        return filteredBookings;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="agent-booking-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/agent')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <ClipboardList className="w-8 h-8 text-indigo-600" />
                Booking Management
              </h1>
              <p className="text-slate-500 mt-1">Manage facility bookings, invoices, and payments</p>
            </div>
            <Button variant="outline" onClick={fetchBookings}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total || 0}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{stats?.pending || 0}</p>
                  <p className="text-xs text-amber-600">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{stats?.invoice_issued || 0}</p>
                  <p className="text-xs text-blue-600">Invoiced</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{stats?.paid || 0}</p>
                  <p className="text-xs text-emerald-600">Confirmed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-indigo-50 border-indigo-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Car className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-indigo-700">{stats?.visitor_parking || 0}</p>
                  <p className="text-xs text-indigo-600">Parking</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700">{stats?.venue_hire || 0}</p>
                  <p className="text-xs text-purple-600">Venue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters & Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by name, unit, booking ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="invoice_issued">Invoice Issued</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterFacility || "all"} onValueChange={(v) => setFilterFacility(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Facilities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Facilities</SelectItem>
                  <SelectItem value="visitor_parking">Visitor Parking</SelectItem>
                  <SelectItem value="venue_hire">Venue Hire</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border mb-4">
            <TabsTrigger value="all">All ({filteredBookings.length})</TabsTrigger>
            <TabsTrigger value="pending" className="text-amber-700">Pending ({filteredBookings.filter(b => b.status === 'pending').length})</TabsTrigger>
            <TabsTrigger value="invoiced">Invoiced ({filteredBookings.filter(b => b.status === 'invoice_issued').length})</TabsTrigger>
            <TabsTrigger value="confirmed">Confirmed ({filteredBookings.filter(b => ['paid', 'confirmed'].includes(b.status)).length})</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {getBookingsByTab().length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Bookings Found</h3>
                  <p className="text-slate-500">Bookings will appear here when residents make requests.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Booking</TableHead>
                      <TableHead>Resident</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getBookingsByTab().map(booking => (
                      <TableRow key={booking.booking_id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              booking.facility_type === 'visitor_parking' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                            }`}>
                              {booking.facility_type === 'visitor_parking' ? <Car className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
                            </div>
                            <div>
                              <p className="font-medium">
                                {booking.facility_type === 'visitor_parking' 
                                  ? `Parking - ${booking.parking_bay}`
                                  : `Venue - ${booking.venue_name}`
                                }
                              </p>
                              <p className="text-xs text-slate-500">#{booking.booking_id.slice(-8)}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{booking.resident_name}</p>
                            <p className="text-sm text-slate-500">Unit {booking.unit_number}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{formatShortDate(booking.start_date)}</p>
                            <p className="text-slate-500">to {formatShortDate(booking.end_date)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {booking.facility_type === 'visitor_parking' ? (
                            <div className="text-sm">
                              <p>Vehicle: {booking.vehicle_registration}</p>
                              {booking.visitor_name && <p className="text-slate-500">Visitor: {booking.visitor_name}</p>}
                            </div>
                          ) : (
                            <div className="text-sm">
                              <p>{booking.purpose}</p>
                              {booking.expected_guests && <p className="text-slate-500">{booking.expected_guests} guests</p>}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(booking.status)}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setSelectedBooking(booking);
                                setShowDetailsDialog(true);
                              }}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              {booking.status === 'pending' && (
                                <DropdownMenuItem onClick={() => {
                                  setSelectedBooking(booking);
                                  setInvoiceForm({
                                    amount: '',
                                    description: booking.facility_type === 'visitor_parking' 
                                      ? `Visitor Parking - ${booking.number_of_nights || 1} night(s)`
                                      : `Venue Hire - ${booking.venue_name}`,
                                    due_date: ''
                                  });
                                  setShowInvoiceDialog(true);
                                }}>
                                  <Receipt className="w-4 h-4 mr-2" />
                                  Generate Invoice
                                </DropdownMenuItem>
                              )}
                              {booking.status === 'invoice_issued' && booking.invoice_id && (
                                <DropdownMenuItem onClick={() => handleResendInvoice(booking.invoice_id)}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Resend Invoice
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleViewAuditLogs(booking.booking_id)}>
                                <History className="w-4 h-4 mr-2" />
                                Audit Log
                              </DropdownMenuItem>
                              {['pending', 'invoice_issued'].includes(booking.status) && (
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => {
                                    if (window.confirm('Cancel this booking?')) {
                                      handleUpdateStatus(booking.booking_id, 'cancelled', 'Cancelled by agent');
                                    }
                                  }}
                                >
                                  <Ban className="w-4 h-4 mr-2" />
                                  Cancel Booking
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Invoice Generation Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-600" />
              Generate Invoice
            </DialogTitle>
            <DialogDescription>
              Create an invoice for {selectedBooking?.resident_name}'s booking
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Booking Summary */}
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <p><strong>Booking:</strong> #{selectedBooking?.booking_id?.slice(-8)}</p>
              <p><strong>Resident:</strong> {selectedBooking?.resident_name} (Unit {selectedBooking?.unit_number})</p>
              <p><strong>Dates:</strong> {selectedBooking && formatDate(selectedBooking.start_date)} - {selectedBooking && formatDate(selectedBooking.end_date)}</p>
            </div>

            <div>
              <Label>Amount (R) *</Label>
              <Input
                type="number"
                value={invoiceForm.amount}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                placeholder="e.g., 150.00"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Input
                value={invoiceForm.description}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                placeholder="e.g., Visitor Parking - 2 nights"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Due Date (Optional)</Label>
              <Input
                type="date"
                value={invoiceForm.due_date}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowInvoiceDialog(false); setSelectedBooking(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateInvoice}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-600" />
              Booking Details
            </DialogTitle>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    selectedBooking.facility_type === 'visitor_parking' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                  }`}>
                    {selectedBooking.facility_type === 'visitor_parking' ? <Car className="w-6 h-6" /> : <Building2 className="w-6 h-6" />}
                  </div>
                  <div>
                    <h3 className="font-semibold">
                      {selectedBooking.facility_type === 'visitor_parking' 
                        ? `Visitor Parking - ${selectedBooking.parking_bay}`
                        : `Venue - ${selectedBooking.venue_name}`
                      }
                    </h3>
                    <p className="text-sm text-slate-500">#{selectedBooking.booking_id}</p>
                  </div>
                </div>
                {getStatusBadge(selectedBooking.status)}
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Resident</p>
                  <p className="font-medium">{selectedBooking.resident_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Unit</p>
                  <p className="font-medium">{selectedBooking.unit_number}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Start Date</p>
                  <p className="font-medium">{formatDate(selectedBooking.start_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">End Date</p>
                  <p className="font-medium">{formatDate(selectedBooking.end_date)}</p>
                </div>
                {selectedBooking.vehicle_registration && (
                  <div>
                    <p className="text-xs text-slate-500">Vehicle</p>
                    <p className="font-medium">{selectedBooking.vehicle_registration}</p>
                  </div>
                )}
                {selectedBooking.visitor_name && (
                  <div>
                    <p className="text-xs text-slate-500">Visitor</p>
                    <p className="font-medium">{selectedBooking.visitor_name}</p>
                  </div>
                )}
                {selectedBooking.purpose && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">Purpose</p>
                    <p className="font-medium">{selectedBooking.purpose}</p>
                  </div>
                )}
                {selectedBooking.expected_guests && (
                  <div>
                    <p className="text-xs text-slate-500">Expected Guests</p>
                    <p className="font-medium">{selectedBooking.expected_guests}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs text-slate-500">Created</p>
                <p className="text-sm">{formatDate(selectedBooking.created_at)}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDetailsDialog(false); setSelectedBooking(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" />
              Audit Log
            </DialogTitle>
          </DialogHeader>

          <div className="max-h-[400px] overflow-y-auto py-4">
            {auditLogs.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No audit logs found</p>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline">{log.action?.replace(/_/g, ' ')}</Badge>
                      <span className="text-xs text-slate-500">{formatDate(log.timestamp)}</span>
                    </div>
                    {log.details && <p className="text-sm text-slate-600">{log.details}</p>}
                    <p className="text-xs text-slate-400 mt-1">By: {log.performed_by}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
